import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Search, CarFrontFill, TrophyFill } from 'react-bootstrap-icons';
import { useSelector } from 'react-redux';
import AdminLayout, { isStaff } from '../AdminLayout';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../../common/ui/StateBlock';
import { currentLanguage } from '../../../common/ui/notify';
import { display, formatPrice, formatUsdFromGel, formatDateTime, coverImage } from '../../../common/format';
import './AdminAuction.css';

const L = { geo: 0, en: 1, ru: 2, am: 3 };
const t = (geo, en, ru, am) => [geo, en, ru, am][L[currentLanguage()] ?? 0];

// How the below-reserve decision flow reads to the operator.
const decisionLabel = (d) => ({
    accepted: t('დადასტურებული', 'Accepted', 'Принято', 'Հաստատված'),
    pending: t('მოლოდინში', 'Pending', 'В ожидании', 'Սպասման մեջ'),
    pending_seller: t('გამყიდველის პასუხი', 'Awaiting seller', 'Ожидает продавца', 'Սպասում է վաճառողին'),
    countered: t('კონტრშეთავაზება', 'Counter-offer', 'Встречное предложение', 'Հակաառաջարկ'),
    rejected: t('უარყოფილი', 'Rejected', 'Отклонено', 'Մերժված'),
}[d] || d || '—');

const decisionTone = (d) => ({
    accepted: 'cx-badge-sold',
    pending: 'cx-badge-new',
    pending_seller: 'cx-badge-new',
    countered: 'cx-badge-new',
    rejected: 'cx-badge-danger',
}[d] || 'cx-badge-neutral');

const invoiceLabel = (s) => ({
    paid: t('გადახდილი', 'Paid', 'Оплачен', 'Վճարված'),
    unpaid: t('გადაუხდელი', 'Unpaid', 'Не оплачен', 'Չվճարված'),
    overdue: t('ვადაგადაცილებული', 'Overdue', 'Просрочен', 'Ժամկետանց'),
    cancelled: t('გაუქმებული', 'Cancelled', 'Отменён', 'Չեղարկված'),
}[s] || null);

const invoiceTone = (s) => ({
    paid: 'cx-badge-sold',
    unpaid: 'cx-badge-new',
    overdue: 'cx-badge-danger',
    cancelled: 'cx-badge-neutral',
}[s] || 'cx-badge-neutral');

const carTitle = (r) => [r.year, r.mark, r.model].filter(Boolean).join(' ') || `#${r.car_id}`;
const winnerName = (r) => {
    if (r.winner_account_type === 'company' && r.winner_company) return r.winner_company;
    return [r.winner_name, r.winner_surname].filter(Boolean).join(' ') || `#${r.winner_id}`;
};

const AdminAuctionWon = () => {
    const user = useSelector((s) => s.user);
    const [rows, setRows] = useState([]);
    const [state, setState] = useState('loading');
    const [q, setQ] = useState('');
    const [eventId, setEventId] = useState('');

    const load = useCallback(() => {
        setState('loading');
        axios.get('/api/auction/admin/won')
            .then((res) => { setRows(res.data.won || []); setState('ready'); })
            .catch(() => setState('error'));
    }, []);

    useEffect(() => { if (isStaff(user)) load(); }, [load, user]);

    // Events present in the result, for the filter dropdown.
    const events = useMemo(() => {
        const map = new Map();
        rows.forEach((r) => { if (r.event_id && !map.has(r.event_id)) map.set(r.event_id, r.event_title || `#${r.event_id}`); });
        return Array.from(map, ([id, title]) => ({ id, title }));
    }, [rows]);

    const query = q.trim().toLowerCase();
    const list = rows.filter((r) => {
        if (eventId && String(r.event_id) !== String(eventId)) return false;
        if (!query) return true;
        return `${carTitle(r)} ${r.vin || ''} ${winnerName(r)} ${r.winner_phone || ''} ${r.winner_email || ''} #${r.lot_id}`
            .toLowerCase().includes(query);
    });

    const totalValue = list.reduce((sum, r) => sum + (Number(r.winning_bid || r.current_price) || 0), 0);

    const body = () => {
        if (state === 'loading') return <LoadingBlock label={t('იტვირთება…', 'Loading…', 'Загрузка…', 'Բեռնվում է…')} />;
        if (state === 'error') return <ErrorBlock title={t('შეცდომა', 'Error', 'Ошибка', 'Սխալ')} message={t('მოგებული მანქანები ვერ ჩაიტვირთა', 'Could not load won cars', 'Не удалось загрузить', 'Չհաջողվեց բեռնել')} retryLabel={t('თავიდან', 'Retry', 'Повторить', 'Կրկին')} onRetry={load} />;
        if (!rows.length) return <EmptyBlock title={t('მოგებული მანქანა არ არის', 'No won cars yet', 'Пока нет выигранных авто', 'Շահած մեքենաներ դեռ չկան')} message={t('ჯერ არცერთი ლოტი არ დასრულებულა გამარჯვებულით.', 'No lot has finished with a winner yet.', 'Ни один лот ещё не завершился победителем.', 'Դեռ ոչ մի լոտ չի ավարտվել հաղթողով։')} />;

        return (
            <>
                <div className="admin-auction-form-row" style={{ marginBottom: 14, gap: 12 }}>
                    <div className="admin-carpicker-search" style={{ flex: '1 1 320px', marginBottom: 0 }}>
                        <Search />
                        <input value={q} onChange={(e) => setQ(e.target.value)}
                            placeholder={t('ძებნა: მანქანა, VIN, გამარჯვებული…', 'Search: car, VIN, winner…', 'Поиск: авто, VIN, победитель…', 'Որոնել՝ մեքենա, VIN, հաղթող…')} />
                    </div>
                    {events.length > 1 && (
                        <select className="cx-select" style={{ flex: '0 1 220px' }} value={eventId} onChange={(e) => setEventId(e.target.value)}>
                            <option value="">{t('ყველა აუქციონი', 'All auctions', 'Все аукционы', 'Բոլոր աճուրդները')}</option>
                            {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
                        </select>
                    )}
                    <span className="admin-table-secondary" style={{ whiteSpace: 'nowrap' }}>
                        {t('სულ', 'Total', 'Всего', 'Ընդամենը')}: {list.length} · {formatPrice(totalValue, 'GEL')}
                    </span>
                </div>

                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>{t('ლოტი', 'Lot', 'Лот', 'Լոտ')}</th>
                                <th>{t('ავტომობილი', 'Car', 'Авто', 'Մեքենա')}</th>
                                <th>{t('გამარჯვებული', 'Winner', 'Победитель', 'Հաղթող')}</th>
                                <th>{t('ბოლო ბიდი', 'Last bid', 'Последняя ставка', 'Վերջին հայտ')}</th>
                                <th>{t('სტატუსი', 'Status', 'Статус', 'Կարգավիճակ')}</th>
                                <th className="admin-hide-sm">{t('ინვოისი', 'Invoice', 'Счёт', 'Հաշիվ')}</th>
                                <th className="admin-hide-sm">{t('მოგების თარიღი', 'Won on', 'Дата выигрыша', 'Շահման ամսաթիվ')}</th>
                                <th className="admin-hide-sm">{t('აუქციონი', 'Auction', 'Аукцион', 'Աճուրդ')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((r) => {
                                const cover = coverImage(r);
                                const bid = r.winning_bid || r.current_price;
                                const usd = r.usd_rate ? formatUsdFromGel(bid, r.usd_rate) : null;
                                const invLabel = invoiceLabel(r.invoice_status);
                                return (
                                    <tr key={r.outcome_id}>
                                        <td className="admin-table-secondary">#{r.lot_id}</td>
                                        <td>
                                            <div className="admin-won-car">
                                                <span className="admin-won-thumb" style={cover ? { backgroundImage: `url('${cover}')` } : {}}>
                                                    {!cover && <CarFrontFill />}
                                                </span>
                                                <span className="admin-won-cartext">
                                                    <strong>{carTitle(r)}</strong>
                                                    {r.vin && <span>{r.vin}</span>}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="admin-table-primary">{winnerName(r)}</span>
                                            <span className="admin-table-secondary">{display(r.winner_phone)}{r.winner_email ? ` · ${r.winner_email}` : ''}</span>
                                        </td>
                                        <td>
                                            <strong>{formatPrice(bid, 'GEL')}</strong>
                                            {usd && <span className="admin-table-secondary">{usd}</span>}
                                        </td>
                                        <td>
                                            <span className={`cx-badge ${decisionTone(r.decision)}`}>{decisionLabel(r.decision)}</span>
                                            {!r.reserve_met && Number(r.reserve_price) > 0 && (
                                                <span className="admin-table-secondary">{t('რეზერვ. ქვემოთ', 'below reserve', 'ниже резерва', 'ռեզերվից ցածր')}</span>
                                            )}
                                        </td>
                                        <td className="admin-hide-sm">
                                            {invLabel ? <span className={`cx-badge ${invoiceTone(r.invoice_status)}`}>{invLabel}</span> : <span className="admin-table-secondary">—</span>}
                                        </td>
                                        <td className="admin-hide-sm admin-table-secondary">{formatDateTime(r.won_at)}</td>
                                        <td className="admin-hide-sm admin-table-secondary">{display(r.event_title)}</td>
                                    </tr>
                                );
                            })}
                            {list.length === 0 && <tr><td colSpan={8} className="admin-table-secondary">{t('შედეგი ვერ მოიძებნა', 'No match', 'Не найдено', 'Չգտնվեց')}</td></tr>}
                        </tbody>
                    </table>
                </div>
            </>
        );
    };

    return (
        <AdminLayout title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}><TrophyFill /> {t('მოგებული მანქანები', 'Won cars', 'Выигранные авто', 'Շահած մեքենաներ')}</span>}
            subtitle={t('ყველა ლოტი, რომელიც გამარჯვებულით დასრულდა', 'Every lot that finished with a winner', 'Все лоты, завершённые с победителем', 'Բոլոր լոտերը, որոնք ավարտվել են հաղթողով')}>
            {body()}
        </AdminLayout>
    );
};

export default AdminAuctionWon;
